import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext, API } from '../../App';
import { MessageCircle, X, Send, Users, ArrowLeft, Search } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { ScrollArea } from './scroll-area';
import axios from 'axios';

const FloatingChat = () => {
  const { token, user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('conversations'); // conversations, chat, new-chat
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/chat/conversations/${conversationId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/chat/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen && token) {
      fetchConversations();
      fetchUsers();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
      const interval = setInterval(() => {
        fetchMessages(selectedConversation.conversation_id);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API}/chat/send`, {
        conversation_id: selectedConversation?.conversation_id,
        message: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages(selectedConversation.conversation_id);
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message');
    }
  };

  const startNewChat = async (recipientId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat/send`, {
        recipient_id: recipientId,
        message: "👋 Hi!"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchConversations();
      const conv = conversations.find(c => c.conversation_id === response.data.conversation_id);
      if (conv) {
        setSelectedConversation(conv);
        setView('chat');
      } else {
        // Fetch fresh conversations and find the new one
        const freshConvs = await axios.get(`${API}/chat/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newConv = freshConvs.data.find(c => c.conversation_id === response.data.conversation_id);
        if (newConv) {
          setSelectedConversation(newConv);
          setConversations(freshConvs.data);
          setView('chat');
        }
      }
    } catch (error) {
      console.error('Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const getOtherParticipant = (conv) => {
    const idx = conv.participants.findIndex(p => p !== user?.user_id);
    return {
      name: conv.participant_names[idx] || 'Unknown',
      role: conv.participant_roles[idx] || ''
    };
  };

  const getRoleBadgeStyle = (role) => {
    const styles = {
      'HQ_ADMIN': 'bg-red-600 text-white',
      'SYSTEM_ADMIN': 'bg-purple-600 text-white',
      'REGIONAL_COORDINATOR': 'bg-blue-600 text-white',
      'FRANCHISEE_OPERATOR': 'bg-emerald-600 text-white',
      'FRANCHISEE_MANAGER': 'bg-cyan-600 text-white',
      'FRANCHISEE_SUPERVISOR': 'bg-slate-500 text-white',
    };
    return styles[role] || 'bg-gray-500 text-white';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(u => 
    u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed bottom-8 right-6 z-50">
      {/* Chat Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="w-80 md:w-96 h-[500px] bg-background border rounded-lg shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-primary text-primary-foreground rounded-t-lg">
            {view === 'conversations' && (
              <>
                <h3 className="font-semibold">Messages</h3>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => setView('new-chat')}>
                    <Users className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => setIsOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            {view === 'chat' && (
              <>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => { setView('conversations'); setSelectedConversation(null); }}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <p className="font-semibold text-sm">{getOtherParticipant(selectedConversation).name}</p>
                    <p className="text-xs opacity-80">{getOtherParticipant(selectedConversation).role.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            {view === 'new-chat' && (
              <>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => setView('conversations')}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-semibold">New Message</span>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary/80" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {/* Conversations List */}
            {view === 'conversations' && (
              <ScrollArea className="h-full">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No conversations yet</p>
                    <Button variant="link" onClick={() => setView('new-chat')}>Start a new chat</Button>
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const other = getOtherParticipant(conv);
                    return (
                      <div
                        key={conv.conversation_id}
                        className={`p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors ${conv.unread_count > 0 ? 'bg-primary/5' : ''}`}
                        onClick={() => { setSelectedConversation(conv); setView('chat'); }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{other.name}</p>
                              {conv.unread_count > 0 && (
                                <Badge className="bg-primary text-xs h-5 px-1.5">{conv.unread_count}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{conv.last_message || 'No messages'}</p>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </ScrollArea>
            )}

            {/* Chat View */}
            {view === 'chat' && selectedConversation && (
              <div className="flex flex-col h-full">
                <ScrollArea className="flex-1 p-3">
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-2 ${
                            msg.sender_id === user?.user_id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Message Input */}
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1"
                    />
                    <Button size="icon" onClick={sendMessage} disabled={!newMessage.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* New Chat - User List */}
            {view === 'new-chat' && (
              <div className="flex flex-col h-full">
                <div className="p-3 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.user_id}
                        className="p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => !loading && startNewChat(u.user_id)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{u.full_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <Badge className={`text-xs ${getRoleBadgeStyle(u.role)}`}>
                            {u.role.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChat;
